/**
 * RSS source adapter.
 * Fetches RSS/Atom feeds via the proxy endpoint (web) or direct (desktop),
 * parses XML with DOMParser, maps items to NormalizedEvent.
 * Integrates circuit breaker and rate limiter.
 */

import type { NormalizedEvent, Source, SourceMeta, SourceStatus, ConfigField } from '@/sources/base-source';
import { RateLimiter } from '@/utils/rate-limit';
import { sanitizeUrl } from '@/utils/sanitize';
import { getApiBase } from '@/app/api-router';

export interface RssAdapterConfig {
  /** Unique ID for this feed instance. */
  id: string;
  /** Display name. */
  name: string;
  /** The RSS/Atom feed URL. */
  feedUrl: string;
  /** Category tag applied to all items from this feed. */
  category: string;
  /** Update interval in ms (default: 300000 = 5min). */
  updateIntervalMs?: number;
  /** ISO 639-1 language code (e.g. 'ar', 'ru', 'zh'). Omit for English feeds. */
  language?: string;
}

function buildFetchUrl(feedUrl: string): string {
  // Routes through sidecar (desktop) or Vercel edge function (web)
  return `${getApiBase()}/api/rss/feed?url=${encodeURIComponent(feedUrl)}`;
}

/** Parse RSS 2.0 or Atom XML into NormalizedEvent items. */
function parseRssXml(xml: string, sourceId: string, sourceName: string, category: string, language?: string): NormalizedEvent[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    console.warn(`[Sentinel] XML parse error for ${sourceId}`);
    return [];
  }

  const events: NormalizedEvent[] = [];

  // Try RSS 2.0 items
  const rssItems = doc.querySelectorAll('item');
  if (rssItems.length > 0) {
    rssItems.forEach(item => {
      const event = parseRssItem(item, sourceId, sourceName, category, language);
      if (event) events.push(event);
    });
    return events;
  }

  // Try Atom entries
  const atomEntries = doc.querySelectorAll('entry');
  atomEntries.forEach(entry => {
    const event = parseAtomEntry(entry, sourceId, sourceName, category, language);
    if (event) events.push(event);
  });

  return events;
}

/**
 * Infer event type from title + description keywords.
 * Returns the best-matching EventType, or 'news' as fallback (never 'custom').
 */
function inferEventType(title: string, description: string): string {
  const text = `${title} ${description}`.toLowerCase();

  // Order matters — more specific patterns first
  const patterns: [string, RegExp][] = [
    ['earthquake', /\b(earthquake|seismic|quake|tremor|richter|magnitude\s+\d)\b/],
    ['fire', /\b(wildfire|bushfire|forest\s+fire|blaze|wildland\s+fire)\b/],
    ['disaster', /\b(tsunami|flood|hurricane|typhoon|cyclone|tornado|landslide|eruption|volcanic|drought|famine)\b/],
    ['weather', /\b(storm|blizzard|heatwave|cold\s+snap|weather\s+warning|monsoon)\b/],
    ['military', /\b(military|army|navy|airforce|troops|battalion|airstrike|missile|drone\s+strike|artillery|defense\s+minister|armed\s+forces|pentagon|nato)\b/],
    ['conflict', /\b(killed|casualties|bombing|shelling|attack|war|combat|offensive|ceasefire|frontline|militia|insurgent|terrorist)\b/],
    ['diplomatic', /\b(diplomat|embassy|ambassador|treaty|accord|summit|negotiations|bilateral|sanctions|foreign\s+minister|un\s+security\s+council)\b/],
    ['sanction', /\b(sanction|sanctioned|embargo|blacklist|ofac|asset\s+freeze|trade\s+ban)\b/],
    ['political', /\b(election|parliament|president|prime\s+minister|legislation|referendum|coup|protest|opposition|government|regime)\b/],
    ['cyber_incident', /\b(cyber|hack|breach|ransomware|malware|ddos|phishing|data\s+leak|zero[\s-]day)\b/],
    ['economic', /\b(gdp|inflation|interest\s+rate|stock\s+market|recession|trade\s+deficit|central\s+bank|monetary\s+policy|tariff|import|export)\b/],
    ['displacement', /\b(refugee|displaced|asylum|migration|evacuat|humanitarian\s+crisis|idp)\b/],
    ['infrastructure', /\b(power\s+outage|grid\s+failure|pipeline|cable\s+cut|internet\s+outage|blackout|infrastructure)\b/],
  ];

  for (const [type, regex] of patterns) {
    if (regex.test(text)) return type;
  }

  return 'news';
}

function parseRssItem(item: Element, sourceId: string, sourceName: string, category: string, language?: string): NormalizedEvent | null {
  const title = getTextContent(item, 'title');
  if (!title) return null;

  const description = getTextContent(item, 'description') ?? undefined;
  const link = getTextContent(item, 'link') ?? undefined;
  const pubDate = getTextContent(item, 'pubDate')
    ?? getNamespacedText(item, 'dc:date')
    ?? getTextContent(item, 'date');
  const guid = getTextContent(item, 'guid') ?? link ?? `${sourceId}-${title}`;

  // Try to extract coordinates from georss:point or geo:lat/geo:long
  const location = extractGeoFromItem(item);

  const timestamp = pubDate ? new Date(pubDate).toISOString() : new Date().toISOString();

  const event: NormalizedEvent = {
    id: `rss-${sourceId}-${hashString(guid)}`,
    source: sourceName,
    timestamp,
    type: inferEventType(title, description ?? '') as any,
    title,
    description: description ? stripHtmlTags(description) : undefined,
    location,
    severity: undefined,
    tags: [category],
    links: link ? [{ url: sanitizeUrl(link), label: 'Source' }] : undefined,
  };

  if (language) event.language = language;

  return event;
}

function parseAtomEntry(entry: Element, sourceId: string, sourceName: string, category: string, language?: string): NormalizedEvent | null {
  const title = getTextContent(entry, 'title');
  if (!title) return null;

  const summary = getTextContent(entry, 'summary') ?? getTextContent(entry, 'content') ?? undefined;
  const linkEl = entry.querySelector('link[href]');
  const link = linkEl?.getAttribute('href') ?? undefined;
  const updated = getTextContent(entry, 'updated') ?? getTextContent(entry, 'published');
  const id = getTextContent(entry, 'id') ?? link ?? `${sourceId}-${title}`;

  const location = extractGeoFromItem(entry);
  const timestamp = updated ? new Date(updated).toISOString() : new Date().toISOString();

  const event: NormalizedEvent = {
    id: `rss-${sourceId}-${hashString(id)}`,
    source: sourceName,
    timestamp,
    type: inferEventType(title, summary ?? '') as any,
    title,
    description: summary ? stripHtmlTags(summary) : undefined,
    location,
    severity: undefined,
    tags: [category],
    links: link ? [{ url: sanitizeUrl(link), label: 'Source' }] : undefined,
  };

  if (language) event.language = language;

  return event;
}

function getTextContent(parent: Element, tag: string): string | null {
  const el = parent.querySelector(tag);
  return el?.textContent?.trim() ?? null;
}

/** Get text from a namespaced element (e.g. dc:date, georss:point). */
function getNamespacedText(parent: Element, qualifiedName: string): string | null {
  const el = parent.getElementsByTagName(qualifiedName)[0];
  return el?.textContent?.trim() ?? null;
}

function extractGeoFromItem(item: Element): NormalizedEvent['location'] {
  // georss:point format: "lat lng"
  const point = item.querySelector('point')?.textContent?.trim();
  if (point) {
    const parts = point.split(/\s+/);
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (isFinite(lat) && isFinite(lng)) {
        return { lat, lng };
      }
    }
  }

  // geo:lat and geo:long
  const geoLat = item.querySelector('lat')?.textContent?.trim();
  const geoLng = item.querySelector('long')?.textContent?.trim();
  if (geoLat && geoLng) {
    const lat = parseFloat(geoLat);
    const lng = parseFloat(geoLng);
    if (isFinite(lat) && isFinite(lng)) {
      return { lat, lng };
    }
  }

  return undefined;
}

/** Strip HTML tags from a string. */
function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

/** Simple hash for generating stable IDs from strings. */
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + ch;
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

/** RSS source adapter implementing the Source interface. */
export class RssAdapter implements Source {
  readonly id: string;
  readonly meta: SourceMeta;
  readonly configSchema: ConfigField[] = [];

  private feedUrl: string;
  private category: string;
  private language: string | undefined;
  private rateLimiter: RateLimiter;
  private status: SourceStatus;

  constructor(config: RssAdapterConfig) {
    this.id = config.id;
    this.feedUrl = config.feedUrl;
    this.category = config.category;
    this.language = config.language;

    this.meta = {
      name: config.name,
      description: `RSS feed: ${config.feedUrl}`,
      icon: '',
      category: 'news',
      updateInterval: config.updateIntervalMs ?? 300_000,
      requiresApiKey: false,
      dataLicense: 'RSS Feed',
      builtIn: false,
    };

    this.rateLimiter = new RateLimiter({
      maxTokens: 2,
      refillRate: 0.1, // 1 token per 10 seconds
    });

    this.status = {
      state: 'initializing',
      eventCount: 0,
    };
  }

  async initialize(): Promise<void> {
    this.status.state = 'active';
  }

  async start(): Promise<void> {
    this.status.state = 'active';
  }

  async stop(): Promise<void> {
    this.status.state = 'disabled';
  }

  async fetch(): Promise<NormalizedEvent[]> {
    if (!this.rateLimiter.tryConsume()) {
      return [];
    }

    try {
      const url = buildFetchUrl(this.feedUrl);
      const response = await globalThis.fetch(url, {
        signal: AbortSignal.timeout(15_000),
      });

      if (!response.ok) {
        throw new Error(`RSS fetch failed: ${response.status}`);
      }

      const xml = await response.text();
      const events = parseRssXml(xml, this.id, this.meta.name, this.category, this.language);

      this.status.state = 'active';
      this.status.lastFetch = new Date().toISOString();
      this.status.eventCount += events.length;
      return events;

    } catch (err) {
      this.status.state = 'error';
      this.status.lastError = err instanceof Error ? err.message : String(err);
      console.warn(`[Sentinel] Fetch failed for ${this.id}:`, err);
      return [];
    }
  }

  getStatus(): SourceStatus {
    return { ...this.status };
  }
}
